import React, { useState, useMemo } from "react";
import { ChevronLeft, ChevronRight, RotateCw, Calendar } from "lucide-react";
import { CompactTaskCard } from "@/components/CompactTaskCard";
import { todayStr, taskOccursOnIso, getOccurrencesOnIso, isTaskSnoozed, isOccurrenceSnoozed } from "@/utils";

const DAY_NAMES = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
const MONTHS = [
    "JANUARY", "FEBRUARY", "MARCH", "APRIL", "MAY", "JUNE",
    "JULY", "AUGUST", "SEPTEMBER", "OCTOBER", "NOVEMBER", "DECEMBER",
];

const fmtISO = (y, m, d) => `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

export const MonthlyView = ({ tasks, attachments, onToggle, onEdit, searchTerm, onPostpone, onUndoPostpone }) => {
    const [anchor, setAnchor] = useState(() => new Date());
    const [selected, setSelected] = useState(todayStr());

    const y = anchor.getFullYear();
    const m = anchor.getMonth();
    const firstDayOfWeek = new Date(y, m, 1).getDay();
    const daysInMonth = new Date(y, m + 1, 0).getDate();

    // Build `iso → tasks[]` by asking each task "do you occur on this day?" — this handles
    // recurring items on ALL their occurrence days plus per-instance overrides (postpone).
    const tasksByDate = useMemo(() => {
        const map = {};
        const now = Date.now();
        for (let dayNum = 1; dayNum <= daysInMonth; dayNum++) {
            const iso = fmtISO(y, m, dayNum);
            const bucket = [];
            for (const t of tasks || []) {
                if (t.archived) continue;
                if (isTaskSnoozed(t, now)) continue;
                if (t.completed && t.type !== "recurring") continue;
                if (t.type === "recurring" && t.interval) {
                    // one row per obligation on this day (native beat + any beats moved here)
                    for (const occ of getOccurrencesOnIso(t, iso)) {
                        if ((t.completions || []).includes(occ.key)) continue;
                        if (isOccurrenceSnoozed(t, occ.key, now)) continue;
                        bucket.push({ ...t, time: occ.time, _occKey: occ.key, _displayIso: occ.displayIso, _movedFrom: occ.movedFrom });
                    }
                    continue;
                }
                if (taskOccursOnIso(t, iso)) bucket.push(t);
            }
            if (bucket.length) map[iso] = bucket;
        }
        for (const k of Object.keys(map)) {
            map[k].sort((a, b) => {
                const aH = a.time ? 0 : 1, bH = b.time ? 0 : 1;
                if (aH !== bH) return aH - bH;
                return (a.time || "").localeCompare(b.time || "");
            });
        }
        return map;
    }, [tasks, y, m, daysInMonth]);

    const selectedTasks = tasksByDate[selected] || [];

    const cells = [];
    for (let i = 0; i < firstDayOfWeek; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);

    return (
        <div className="fade-up" data-testid="monthly-view">
            <div className="rounded-xl border border-meye-border bg-meye-card/60 backdrop-blur-sm p-3 mt-2">
                <div className="flex items-center justify-between mb-2">
                    <button
                        onClick={() => setAnchor(new Date(y, m - 1, 1))}
                        className="w-8 h-8 rounded-lg border border-meye-border bg-black/40 text-zinc-400 hover:text-white flex items-center justify-center"
                        data-testid="monthly-prev"
                    >
                        <ChevronLeft size={16} />
                    </button>
                    <div className="text-center">
                        <h3 className="font-brand text-lg font-extrabold uppercase bg-gradient-to-r from-meye-fuchsia to-violet-400 bg-clip-text text-transparent tracking-wide">
                            {MONTHS[m]} {y}
                        </h3>
                        <button
                            onClick={() => {
                                setAnchor(new Date());
                                setSelected(todayStr());
                            }}
                            className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-meye-cyan mt-0.5 font-mono"
                        >
                            go to today
                        </button>
                    </div>
                    <button
                        onClick={() => setAnchor(new Date(y, m + 1, 1))}
                        className="w-8 h-8 rounded-lg border border-meye-border bg-black/40 text-zinc-400 hover:text-white flex items-center justify-center"
                        data-testid="monthly-next"
                    >
                        <ChevronRight size={16} />
                    </button>
                </div>
                <div className="grid grid-cols-7 gap-1">
                    {DAY_NAMES.map((n) => (
                        <div key={n} className="text-center text-[9px] uppercase tracking-widest text-zinc-500 font-mono py-1">
                            {n}
                        </div>
                    ))}
                    {cells.map((c, i) => {
                        if (!c) return <div key={`e${i}`} />;
                        const iso = fmtISO(y, m, c);
                        const isToday = iso === todayStr();
                        const isSelected = iso === selected;
                        const dayTasks = tasksByDate[iso] || [];
                        const recurringCount = dayTasks.filter((t) => t.type === "recurring" && t.interval).length;
                        const regularCount   = dayTasks.length - recurringCount;
                        const count = dayTasks.length;
                        const bg = isSelected
                            ? "border-violet-400/70 bg-violet-500/20"
                            : isToday
                                ? "border-meye-fuchsia/70 bg-meye-fuchsia/15 hover:bg-meye-fuchsia/25"
                                : count > 0
                                    ? "border-meye-cyan/60 bg-meye-cyan/15 hover:bg-meye-cyan/25"
                                    : "border-meye-border bg-black/30 hover:border-zinc-600";
                        return (
                            <button
                                key={iso}
                                onClick={() => setSelected(iso)}
                                className={`aspect-square rounded-lg border flex flex-col items-center justify-center transition-colors ${bg} px-0.5`}
                                data-testid={`monthly-cell-${iso}`}
                                data-today={isToday}
                                title={count > 0 ? `${regularCount} regular · ${recurringCount} recurring` : ""}
                            >
                                <span className={`font-brand font-bold ${isToday ? "text-[9px] tracking-widest" : "text-xs"} ${isSelected || isToday || count > 0 ? "text-white" : "text-zinc-400"}`}>
                                    {isToday ? "TODAY" : c}
                                </span>
                                {/* Split badge — Calendar (amber) for regular one-shot tasks vs
                                    RotateCw (cyan) for recurring cadences. Smaller sized to fit
                                    a 7-across phone grid. */}
                                {count > 0 && (
                                    <div className="flex items-center justify-center flex-wrap gap-x-1.5 gap-y-0 mt-0.5 leading-none" data-testid={`monthly-count-${iso}`}>
                                        {regularCount > 0 && (
                                            <span className={`inline-flex items-center gap-0.5 text-[11px] font-brand font-bold ${
                                                isSelected ? "text-white" : "text-amber-400"
                                            }`}>
                                                <Calendar size={10} strokeWidth={2.25} />
                                                {regularCount}
                                            </span>
                                        )}
                                        {recurringCount > 0 && (
                                            <span className={`inline-flex items-center gap-0.5 text-[11px] font-brand font-bold ${
                                                isSelected ? "text-white" : "text-cyan-400"
                                            }`}>
                                                <RotateCw size={10} strokeWidth={2.25} />
                                                {recurringCount}
                                            </span>
                                        )}
                                    </div>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* selected-day tasks */}
            <div className="mt-4">
                <h4 className="text-[11px] font-brand font-bold uppercase tracking-widest text-zinc-300 mb-2 flex items-center gap-2">
                    <span>
                        {(() => {
                            const [yy, mm, dd] = selected.split("-").map(Number);
                            const d = new Date(yy, mm - 1, dd);
                            return d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
                        })()}
                    </span>
                    {selected === todayStr() && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded border border-meye-fuchsia/50 bg-meye-fuchsia/10 text-meye-fuchsia">
                            Today
                        </span>
                    )}
                    {selectedTasks.length > 0 && (
                        <span className="ml-auto text-[10px] font-mono text-meye-cyan" data-testid="monthly-selected-count">
                            {selectedTasks.length} {selectedTasks.length === 1 ? "task" : "tasks"}
                        </span>
                    )}
                </h4>
                {selectedTasks.length === 0 ? (
                    <div className="rounded border border-meye-border/50 bg-black/20 px-3 py-1.5 text-[11px] text-zinc-600 font-mono">
                        · nothing scheduled
                    </div>
                ) : (
                    <div className="space-y-1.5">
                        {selectedTasks.map((t) => (
                            <CompactTaskCard
                                key={`${t.id}:${t._occKey || ""}`}
                                task={t}
                                attachments={attachments}
                                occurrenceIso={t._occKey || selected}
                                onToggle={onToggle}
                                onEdit={(row) => onEdit({ ...row, _occurrenceDate: selected })}
                                searchTerm={searchTerm}
                                onPostpone={onPostpone}
                                onUndoPostpone={onUndoPostpone}
                                undoFromIso={t._movedFrom || null}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MonthlyView;
